import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import { Reveal, Eyebrow, HeroLines } from "@/components/Reveal";

const JOURNEY = [
  { mark: "Childhood", text: "Painting, sculpting and drawing — the love of making art was fostered early, and it never let go." },
  { mark: "High school", text: "A first stone sculpture. It still sits in Richard's Oregon home today." },
  { mark: "College years", text: "A cherished stoneware sculpture and a watercolor portrait both remain in his home for family and guests to enjoy." },
  { mark: "Today", text: "His studio in the Willamette Valley is surrounded by fields and hills. The perfect environment for painting the feelings and memories of places visited." },
  { mark: "The card table", text: "Small original watercolors, painted one at a time — because original art should be within everyone's reach." },
];

const PIGMENTS = [
  { name: "Burnt Umber", hex: "#4A3B2C", note: "The shadow beneath everything — warm, deep, honest." },
  { name: "Yellow Ochre", hex: "#C99736", note: "Late light on pasture and stubble fields." },
  { name: "Ultramarine Blue", hex: "#3A53A4", note: "Big sky blue — the whole height of a clear afternoon." },
  { name: "Olive Green", hex: "#6F7D3B", note: "Fields and hedgerows after a rain." },
  { name: "Lemon Yellow", hex: "#F0CE3E", note: "Sunbreaks and seed heads — the first touch of light." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo title="About the Artist" description="Richard Kuensting paints the remembered feelings of places he has visited — windy days, foggy mornings, peaceful afternoons — from his studio in Oregon's Willamette Valley." />
      <header className="max-w-7xl mx-auto px-5 sm:px-10 pt-16 sm:pt-24 pb-12">
        <Eyebrow>About the Studio</Eyebrow>
        <HeroLines lines={["Windy days,", "foggy mornings,", "peaceful afternoons."]}
          className="font-serif font-light text-ink text-4xl sm:text-6xl mt-4 leading-[1.08] tracking-tight" />
        <p className="text-stone2 mt-6 max-w-xl text-sm sm:text-base leading-relaxed">
          In Richard&rsquo;s own words — what he paints, and why.
        </p>
      </header>

      <section className="max-w-7xl mx-auto px-5 sm:px-10 pb-24 grid lg:grid-cols-12 gap-12 items-start">
        <Reveal className="lg:col-span-5">
          <img src="/images/studio-inside.jpg" alt="Richard Kuensting painting at his studio bench — brushes, palettes and the window light around him"
            className="w-full aspect-[4/5] object-cover border border-ink/10 deckle" />
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone2 mt-3">Fig. 01 — Richard Kuensting, in the studio</p>
        </Reveal>
        <div className="lg:col-span-7 space-y-6">
          <Reveal>
            <p className="text-stone2 leading-relaxed text-sm sm:text-base">
              Richard has been making art since childhood — painting, sculpting and drawing. A stone sculpture from his high-school years and portraits in ceramic and watercolor from college still grace his home in Oregon; the making never stopped.
            </p>
            <p className="text-stone2 leading-relaxed text-sm sm:text-base">
              His studio sits in the Willamette Valley, surrounded by fields and hills. His paintings begin with places he has visited — gathered as sketches, memory, and reference photographs — but what arrives on the paper is the remembered feeling of them: windy days, foggy mornings, and peaceful afternoons.
            </p>
            <p className="text-stone2 leading-relaxed text-sm sm:text-base">
              And one belief shapes everything on this site: original art should be reasonably available to anyone. His notecards are small, signed originals — works of art meant to be gifted, suitable for framing, made to hold a timeless beauty.
            </p>
          </Reveal>
          <Reveal>
            <img src="/images/studio-table.jpg" alt="Richard's real working table — brush jars, well-used palettes, paint-stained rag and blue test strips"
              className="w-full aspect-[3/4] object-cover border border-ink/10 deckle" />
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone2 mt-3">Fig. 02 — The working table, photographed in the studio</p>
          </Reveal>
        </div>
      </section>

      {/* FROM THE FIELD BOOK — Richard's process photos */}
      <section className="max-w-7xl mx-auto px-5 sm:px-10 pb-24" data-testid="fieldbook-section">
        <Reveal>
          <Eyebrow>From the Field Book</Eyebrow>
          <h2 className="font-serif text-3xl sm:text-4xl text-ink mt-4 leading-tight">First marks, then paint</h2>
        </Reveal>
        <div className="grid md:grid-cols-12 gap-6 mt-10 items-start">
          <Reveal className="md:col-span-7">
            <img src="/images/process-flatlay.jpg" alt="The working table — field sketch with numbered divisions, finished watercolor card, and envelopes"
              loading="lazy" className="w-full border border-ink/10 deckle" />
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone2 mt-3">Fig. 03 — Sketch, working notes, finished card, and its envelope</p>
          </Reveal>
          <Reveal delay={0.12} className="md:col-span-5">
            <img src="/images/process-framed.jpg" alt="The finished watercolor, framed with a wide mat and wood frame" data-testid="process-framed-photo"
              loading="lazy" className="w-full border border-ink/10 deckle" />
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone2 mt-3">Fig. 04 — The finished piece, framed</p>
          </Reveal>
        </div>
        <div className="grid md:grid-cols-12 gap-6 mt-6 items-start">
          <Reveal delay={0.08} className="md:col-span-5">
            <img src="/images/field-sketching.jpg" alt="Richard taking field notes on the Oregon coast, sketchbook in hand" data-testid="field-sketching-photo"
              loading="lazy" className="w-full border border-ink/10 deckle" />
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone2 mt-3">Fig. 05 — Field notes on the coast, sketchbook in hand</p>
          </Reveal>
          <Reveal delay={0.18} className="md:col-span-7">
            <img src="/images/field-view.jpg" alt="The morning's subject — tide flats, the headland, and first light" data-testid="field-view-photo"
              loading="lazy" className="w-full border border-ink/10 deckle" />
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone2 mt-3">Fig. 06 — The morning's subject, first light on the tide flats</p>
          </Reveal>
        </div>
        <div className="mt-10 md:w-1/2 md:mx-auto">
          <Reveal delay={0.08}>
            <div className="grid grid-cols-2 gap-3 items-start">
              <img src="/images/field-beginning.jpg" alt="The beginning — paper taped down, first washes laid on location" data-testid="field-beginning-photo"
                loading="lazy" className="w-full border border-ink/10 deckle" />
              <img src="/images/field-finished.jpg" alt="The same painting, finished that day — signed and dated Sept 2026" data-testid="field-finished-photo"
                loading="lazy" className="w-full border border-ink/10 deckle" />
            </div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone2 mt-3 text-center" data-testid="field-arc-caption">
              The day finished, location to studio — sketch, first wash, final result
            </p>
          </Reveal>
        </div>
      </section>

      {/* IN THE STUDIO — hands + window view */}
      <section className="max-w-7xl mx-auto px-5 sm:px-10 pb-24" data-testid="in-the-studio-section">
        <Reveal>
          <Eyebrow>In the Studio</Eyebrow>
          <h2 className="font-serif text-3xl sm:text-4xl text-ink mt-4 leading-tight">The work, up close</h2>
        </Reveal>
        <div className="grid md:grid-cols-12 gap-6 mt-10 items-start">
          <Reveal className="md:col-span-7">
            <img src="/images/studio-window.jpg" alt="Richard painting at his bench by the studio window, the yard and fence beyond" data-testid="studio-window-photo"
              loading="lazy" className="w-full border border-ink/10 deckle" />
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone2 mt-3">Fig. 07 — The studio window, fields and hills beyond the yard</p>
          </Reveal>
          <Reveal delay={0.12} className="md:col-span-5">
            <img src="/images/studio-hands.jpg" alt="Richard's hands mixing a wash and painting a small landscape card" data-testid="studio-hands-photo"
              loading="lazy" className="w-full border border-ink/10 deckle" />
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone2 mt-3">Fig. 08 — Every wash mixed by hand, one card at a time</p>
          </Reveal>
        </div>
        <div className="grid md:grid-cols-2 gap-6 mt-10 items-start">
          <Reveal delay={0.08}>
            <img src="/images/studio-framed-sea.jpg" alt="The September grey-sea watercolor, framed with a wide mat and slate inner border" data-testid="studio-framed-sea-photo"
              loading="lazy" className="w-full border border-ink/10 deckle" />
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone2 mt-3">Fig. 09 — September's grey sea, framed</p>
          </Reveal>
          <Reveal delay={0.18}>
            <img src="/images/studio-framed-hill.jpg" alt="A September hillside watercolor with passing rain, framed with a wide mat and sage inner border" data-testid="studio-framed-hill-photo"
              loading="lazy" className="w-full border border-ink/10 deckle" />
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone2 mt-3">Fig. 10 — September rain on the hill, framed</p>
          </Reveal>
        </div>
      </section>

      <Marquee />

      <section className="max-w-7xl mx-auto px-5 sm:px-10 py-24 grid lg:grid-cols-2 gap-16">
        <Reveal>
          <Eyebrow>The Road So Far</Eyebrow>
          <h2 className="font-serif text-3xl sm:text-4xl text-ink mt-4">A maker&rsquo;s journey</h2>
          <ol className="mt-10 space-y-0 border-l border-ink/15">
            {JOURNEY.map((t) => (
              <li key={t.mark} className="relative pl-8 pb-10 last:pb-0">
                <span className="absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full bg-terracotta" />
                <p className="font-mono text-xs tracking-[0.25em] uppercase text-terracotta">{t.mark}</p>
                <p className="text-sm text-stone2 leading-relaxed mt-2 max-w-md">{t.text}</p>
              </li>
            ))}
          </ol>
        </Reveal>
        <Reveal delay={0.15}>
          <Eyebrow>The Water</Eyebrow>
          <h2 className="font-serif text-3xl sm:text-4xl text-ink mt-4">Rich color washes, one treasure at a time.</h2>
          <ul className="mt-10 space-y-4">
            {PIGMENTS.map((p) => (
              <li key={p.name} className="flex items-center gap-5 bg-paper border border-ink/10 rounded-md p-5 deckle">
                <span className="w-12 h-12 rounded-full border border-ink/15 shrink-0" style={{ backgroundColor: p.hex }} />
                <div>
                  <p className="font-serif text-xl text-ink">{p.name}</p>
                  <p className="text-sm text-stone2 mt-0.5">{p.note}</p>
                </div>
              </li>
            ))}
          </ul>
        </Reveal>
      </section>

      <section className="bg-night text-linen">
        <div className="spotlight">
        <div className="max-w-4xl mx-auto px-5 sm:px-10 py-24 text-center">
          <Reveal>
            <Eyebrow className="text-ochre">In His Own Words</Eyebrow>
            <p className="font-serif text-3xl sm:text-4xl leading-snug mt-6 text-balance">
              &ldquo;My paintings are about remembered feelings of places I have visited. I paint to capture windy days, foggy mornings and peaceful afternoons.&rdquo;
            </p>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-linen/50 mt-6">— Richard Kuensting</p>
            <Link to="/contact" data-testid="about-contact-cta"
              className="mt-10 inline-flex items-center gap-2 bg-linen text-ink rounded-full px-8 py-4 text-xs font-mono uppercase tracking-[0.2em] hover:bg-ochre hover:text-night transition-colors duration-300">
              Write to the Studio <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
        </div>
      </section>
    </div>
  );
}
